import React from 'react';
import {isMobile} from 'react-device-detect';
import {IDENTITY_VERIFICATION_PHOTOS_MAX_SIZE_MB} from '@chekin/core';
import {
  WebcamRefTypes,
  WebcamProps,
  MobileWebcam,
  MobileCameraProps,
} from '@chekin/guestapp-ui-kit';
import {useErrorModal} from '../../../hooks';
import {WebcamStyled} from '../styled';
import {PlaceholderImage} from './styled';

const VisibleHelperImageTimeoutMS = 1250;

type TakePhotoProps = Omit<
  WebcamProps &
    MobileCameraProps & {
      mobileHelperText?: React.ReactNode | string;
      mobileHelperTitle?: React.ReactNode | string;
      placeholderImage?: string;
      layoutMode?: string;
    },
  'onError'
>;
const TakePhoto = React.forwardRef<WebcamRefTypes, TakePhotoProps>(
  (
    {
      placeholderImage,
      onScreenshot,
      mobileHelperText,
      mobileHelperTitle,
      disabled,
      layoutMode = 'default',
      ...props
    },
    ref,
  ) => {
    const [isPlaceholderImageVisible, setIsPlaceholderImageVisible] =
      React.useState(false);
    const {displayError, ErrorModal} = useErrorModal();

    const onUserMedia = React.useCallback(() => {
      setIsPlaceholderImageVisible(true);
    }, []);

    const webcamProps = React.useMemo(
      () => ({
        ...props,
        minScreenshotW: 800,
        minScreenshotH: 600,
        screenshotMaxSize: IDENTITY_VERIFICATION_PHOTOS_MAX_SIZE_MB,
        imageSmoothing: false,
        onError: displayError,
        onUserMedia,
      }),
      [onUserMedia, displayError, props],
    );

    React.useEffect(() => {
      if (!isPlaceholderImageVisible) return;
      const timeout = setTimeout(() => {
        setIsPlaceholderImageVisible(false);
      }, VisibleHelperImageTimeoutMS);

      return () => {
        clearTimeout(timeout);
      };
    }, [isPlaceholderImageVisible]);

    if (isMobile && layoutMode === 'default') {
      return (
        <MobileWebcam
          ref={ref}
          disabled={disabled}
          onScreenshot={onScreenshot}
          text={mobileHelperText}
          title={mobileHelperTitle}
          cropToScanArea={!props.roundedScanArea}
          placeholderImage={
            placeholderImage ? (
              <PlaceholderImage
                $visible={isPlaceholderImageVisible}
                src={placeholderImage}
                alt="Placeholder"
              />
            ) : null
          }
          {...webcamProps}
        />
      );
    }

    return (
      <>
        <WebcamStyled
          ref={ref}
          cropToScanArea
          placeholderImage={
            placeholderImage ? (
              <PlaceholderImage
                $visible={isPlaceholderImageVisible}
                src={placeholderImage}
                alt="Placeholder"
              />
            ) : null
          }
          {...webcamProps}
        />
        <ErrorModal />
      </>
    );
  },
);

export {TakePhoto};
